/**
 * Student Portal — My Requests.
 * Real requests from GET /api/orders (backend already scopes to student).
 * Uses a compact status progression bar.
 */
import React, { useState } from 'react';
import { ClipboardList, Ban } from 'lucide-react';
import { Card, EmptyState, StatusPill, orderStatusPill } from '../ui';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';

const PROGRESS = ['pending', 'approved', 'issued', 'completed'];

const progressIndex = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'pending') return 0;
  if (s === 'approved') return 1;
  if (s === 'issued') return 2;
  if (s === 'completed' || s === 'returned') return 3;
  return -1;
};

const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

export default function RequestsPage({ ctx }) {
  const { orders, refresh, showToast, openNewRequest } = ctx;
  const [busyId, setBusyId] = useState(null);

  const cancelRequest = async (orderId) => {
    if (!window.confirm('Cancel this pending request? This cannot be undone.')) return;
    setBusyId(orderId);
    try {
      await apiClient.put(endpoints.orders.cancel(orderId));
      await refresh.orders();
      showToast('Request cancelled.', 'ok');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not cancel this request.', 'bad');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div>
        <span className="sp-eyebrow">Portal</span>
        <h1 className="sp-page-title" data-testid="portal-requests-title">My Requests</h1>
        <p className="sp-page-lede">
          Every request you have submitted, with a clear progression from Pending → Approved → Issued → Returned.
        </p>
      </div>

      <div className="sp-mt-5">
        {orders.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="No requests yet"
            message="Submit your first request from the Home page or the Component Library."
            action={(
              <button
                type="button"
                className="sp-btn sp-btn-primary"
                onClick={() => openNewRequest()}
                data-testid="requests-empty-cta"
              >Request a Component</button>
            )}
            testId="requests-empty"
          />
        ) : (
          <div data-testid="requests-list">
            {orders.map(o => {
              const pill = orderStatusPill(o.status);
              const idx = progressIndex(o.status);
              const isRejected = (o.status || '').toLowerCase() === 'rejected';
              const isCancelled = (o.status || '').toLowerCase() === 'cancelled';
              return (
                <article key={o.id} className="sp-request-row" data-testid={`request-row-${o.id}`}>
                  <header className="sp-request-header">
                    <div style={{ minWidth: 0 }}>
                      <div className="sp-request-id">REQ-{o.id.slice(-8).toUpperCase()} · {formatDate(o.created_at)}</div>
                      <h4 className="sp-request-patient">
                        {o.patient_info?.name || 'Unnamed patient'}
                        <span className="sp-subtle" style={{ marginLeft: 10, fontWeight: 400 }}>
                          PID {o.patient_info?.pid || '—'}
                        </span>
                      </h4>
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
                      {(o.status || '').toLowerCase() === 'pending' && (
                        <button
                          type="button"
                          className="sp-btn sp-btn-ghost sp-btn-sm"
                          disabled={busyId === o.id}
                          onClick={() => cancelRequest(o.id)}
                          data-testid={`request-cancel-btn-${o.id}`}
                        >
                          <Ban size={13} /> {busyId === o.id ? 'Cancelling…' : 'Cancel'}
                        </button>
                      )}
                    </div>
                  </header>

                  {/* Progress bar */}
                  {!isCancelled && !isRejected && (
                    <>
                      <div className="sp-progress" data-testid={`request-progress-${o.id}`}>
                        {PROGRESS.map((_, i) => (
                          <div
                            key={i}
                            className={`sp-progress-step${i <= idx ? ' is-done' : ''}`}
                          />
                        ))}
                      </div>
                      <div className="sp-progress-labels">
                        <span className={`sp-progress-label${idx >= 0 ? ' is-active' : ''}`}>Pending</span>
                        <span className={`sp-progress-label${idx >= 1 ? ' is-active' : ''}`}>Approved</span>
                        <span className={`sp-progress-label${idx >= 2 ? ' is-active' : ''}`}>Issued</span>
                        <span className={`sp-progress-label${idx >= 3 ? ' is-active' : ''}`}>Returned</span>
                      </div>
                    </>
                  )}
                  {isRejected && (
                    <div className="sp-mt-4" style={{ padding: 14, background: 'var(--diis-danger-soft)', color: 'var(--diis-danger)', borderRadius: 'var(--diis-radius)', fontSize: 13 }} data-testid={`request-reject-${o.id}`}>
                      <strong style={{ fontWeight: 600 }}>Rejected.</strong>
                      {o.reason && <div style={{ marginTop: 4 }}>{o.reason}</div>}
                    </div>
                  )}

                  {/* Items */}
                  <div className="sp-request-items">
                    {(o.items || []).map((it, i) => (
                      <div className="sp-item-line" key={i}>
                        <span>
                          <strong>{it.component_name || 'Component'}</strong>
                          <span className="sp-subtle" style={{ marginLeft: 8 }}>
                            {it.brand_name}{it.size ? ` · ${it.size}` : ''}
                          </span>
                        </span>
                        <span className="sp-mono-num">Qty {it.quantity}</span>
                      </div>
                    ))}
                  </div>

                  {o.admin_notes && (
                    <div className="sp-mt-3" style={{ padding: 12, background: 'var(--diis-accent-soft)', color: 'var(--diis-accent-ink)', borderRadius: 'var(--diis-radius)', fontSize: 13 }}>
                      <strong style={{ fontWeight: 600 }}>Faculty note.</strong> {o.admin_notes}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
